import { useState, useEffect } from 'react';
import axios from 'axios';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { Search, RefreshCw, ExternalLink, FileText } from 'lucide-react';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000' });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const TYPE_META = {
  CIRCULAR:      { bg: '#FEE2E2', text: '#991B1B' },
  REGULATION:    { bg: '#FFFBEB', text: '#92400E' },
  DIRECTIVE:     { bg: '#FFFBEB', text: '#92400E' },
  PRESS_RELEASE: { bg: '#EFF6FF', text: '#1E40AF' },
  CONSULTATION:  { bg: '#EEF2FF', text: '#3730A3' },
  GUIDANCE:      { bg: '#F0FDF4', text: '#166534' },
  FAQ:           { bg: '#F3F4F6', text: '#374151' },
};

const SOURCE_LABELS = {
  CSSF_WEBSITE: 'CSSF',
  ESMA:         'ESMA',
  EBA:          'EBA',
  LEGILUX:      'Legilux',
};

const getPresetDates = (preset) => {
  const now = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  switch (preset) {
    case '7d':   return { from: fmt(new Date(now - 7  * 86400000)), to: fmt(now) };
    case '30d':  return { from: fmt(new Date(now - 30 * 86400000)), to: fmt(now) };
    case '90d':  return { from: fmt(new Date(now - 90 * 86400000)), to: fmt(now) };
    case 'year': return { from: fmt(new Date(now.getFullYear(), 0, 1)), to: fmt(now) };
    default:     return { from: '', to: '' };
  }
};

const RegulatoryMonitor = () => {
  const isMobile  = useMobile();
  const { theme } = useTheme();
  const user      = JSON.parse(localStorage.getItem('user') || '{}');

  const [documents,    setDocuments]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [scraping,     setScraping]     = useState(false);
  const [error,        setError]        = useState(null);
  const [scrapeMsg,    setScrapeMsg]    = useState(null);
  const [filterType,   setFilterType]   = useState('ALL');
  const [filterSource, setFilterSource] = useState('ALL');
  const [search,       setSearch]       = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [preset,       setPreset]       = useState('all');

  const handlePreset = (p) => {
    setPreset(p);
    const { from, to } = getPresetDates(p);
    setDateFrom(from); setDateTo(to);
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await API.get('/regulatory/documents', { params: { limit: 2000 } });
      setDocuments(res.data.documents || []);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load documents'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runScraper = async () => {
    setScraping(true); setScrapeMsg(null);
    try {
      await API.post('/regulatory/scrape-now');
      setScrapeMsg({ type: 'success', text: 'Scrape started — refreshing in 5 seconds…' });
      setTimeout(async () => {
        await load();
        setScrapeMsg({ type: 'success', text: 'Scrape complete — documents updated' });
      }, 5000);
    } catch (err) {
      setScrapeMsg({ type: 'error', text: err.response?.data?.error || 'Scrape failed' });
    } finally { setScraping(false); }
  };

  const types   = ['ALL', ...new Set(documents.map(d => d.document_type).filter(Boolean))];
  const sources = ['ALL', ...new Set(documents.map(d => d.source).filter(Boolean))];

  const filtered = documents.filter(d => {
    if (filterType   !== 'ALL' && d.document_type !== filterType)   return false;
    if (filterSource !== 'ALL' && d.source        !== filterSource) return false;
    if (search && !d.title?.toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && d.published_at && new Date(d.published_at) < new Date(dateFrom)) return false;
    if (dateTo   && d.published_at && new Date(d.published_at) > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  const bindingCount = documents.filter(d => d.is_binding).length;

  const inputStyle = {
    padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${theme.border}`, fontSize: 13,
    color: theme.textPrimary, background: theme.bgInput,
    outline: 'none', cursor: 'pointer',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>
            Regulatory Intelligence Monitor
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
            CSSF · ESMA · EBA · Legilux — updated daily at 06:00
          </p>
        </div>
        {user.role === 'ADMIN' && (
          <button onClick={runScraper} disabled={scraping} style={{
            background: scraping ? theme.bgBadge : '#3B82F6',
            color: scraping ? theme.textTertiary : 'white',
            border: 'none', borderRadius: 8, padding: '9px 18px',
            fontWeight: 700, fontSize: 13, cursor: scraping ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={14} style={{ animation: scraping ? 'spin 1s linear infinite' : 'none' }} />
            {scraping ? 'Scraping…' : 'Scrape Now'}
          </button>
        )}
      </div>

      {/* Scrape message */}
      {scrapeMsg && (
        <div style={{ background: scrapeMsg.type === 'success' ? '#F0FDF4' : '#FEF2F2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, border: `1px solid ${scrapeMsg.type === 'success' ? '#BBF7D0' : '#FECACA'}` }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: scrapeMsg.type === 'success' ? '#166534' : '#DC2626' }}>
            {scrapeMsg.text}
          </p>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total',       value: documents.length,                color: '#3B82F6' },
          { label: 'Binding',     value: bindingCount,                    color: '#DC2626' },
          { label: 'Non-Binding', value: documents.length - bindingCount, color: '#16A34A' },
          { label: 'Sources',     value: sources.length - 1,              color: '#8B5CF6' },
        ].map(s => (
          <div key={s.label} style={{
            background: theme.bgCard, borderRadius: 10,
            padding: '16px 20px', border: `1px solid ${theme.border}`,
            boxShadow: theme.shadowSm,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FileText size={18} color={s.color} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.textPrimary, lineHeight: 1 }}>{s.value}</p>
              <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: theme.bgCard, borderRadius: 10, padding: '12px 14px', marginBottom: 14, border: `1px solid ${theme.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <Search size={13} color={theme.textTertiary} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input type="text" placeholder="Search documents…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 30, width: '100%', boxSizing: 'border-box' }} />
        </div>

        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={inputStyle}>
          {types.map(t => <option key={t} value={t}>{t === 'ALL' ? 'All Types' : t.replace(/_/g, ' ')}</option>)}
        </select>

        <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={inputStyle}>
          {sources.map(s => <option key={s} value={s}>{s === 'ALL' ? 'All Sources' : SOURCE_LABELS[s] || s}</option>)}
        </select>

        <select value={preset} onChange={e => handlePreset(e.target.value)} style={inputStyle}>
          <option value="all">All time</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="year">This year</option>
          <option value="custom">Custom range</option>
        </select>

        {preset === 'custom' && (
          <>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
            <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={inputStyle} />
          </>
        )}

        <span style={{ fontSize: 12, color: theme.textTertiary, whiteSpace: 'nowrap' }}>
          {filtered.length} of {documents.length}
        </span>
      </div>

      {/* Document list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14 }}>Loading documents…</div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#DC2626', fontSize: 14 }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14, background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          No documents found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(doc => {
            const typeMeta = TYPE_META[doc.document_type] || { bg: theme.bgBadge, text: theme.textSecondary };
            return (
              <div key={doc.id} style={{
                background: theme.bgCard, borderRadius: 10,
                padding: isMobile ? '12px 14px' : '14px 18px',
                border: `1px solid ${theme.border}`,
                borderLeft: `3px solid ${doc.is_binding ? '#DC2626' : theme.border}`,
                boxShadow: theme.shadowSm, transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#3B82F6'}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: 5, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{ background: typeMeta.bg, color: typeMeta.text, padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>
                        {doc.document_type?.replace(/_/g, ' ') || 'OTHER'}
                      </span>
                      <span style={{ background: '#EFF6FF', color: '#1D4ED8', padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>
                        {SOURCE_LABELS[doc.source] || doc.source}
                      </span>
                      {doc.is_binding && (
                        <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>
                          BINDING
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <a href={doc.source_url} target="_blank" rel="noopener noreferrer" style={{
                      color: theme.textPrimary, fontWeight: 600, fontSize: isMobile ? 13 : 14,
                      textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.4,
                    }}
                      onMouseEnter={e => e.currentTarget.style.color = '#3B82F6'}
                      onMouseLeave={e => e.currentTarget.style.color = theme.textPrimary}
                    >
                      {doc.title}
                      <ExternalLink size={12} style={{ flexShrink: 0, marginTop: 3, opacity: 0.5 }} />
                    </a>

                    {/* Summary */}
                    {doc.summary && !isMobile && (
                      <p style={{ margin: '6px 0 0', fontSize: 12, color: theme.textSecondary, lineHeight: 1.6 }}>
                        {doc.summary.length > 200 ? `${doc.summary.substring(0, 200)}…` : doc.summary}
                      </p>
                    )}
                  </div>

                  {/* Date */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ margin: 0, fontSize: 11, color: theme.textSecondary, whiteSpace: 'nowrap', fontWeight: 500 }}>
                      {doc.published_at
                        ? new Date(doc.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                        : 'Date unknown'}
                    </p>
                    {!isMobile && (
                      <p style={{ margin: '3px 0 0', fontSize: 10, color: theme.textTertiary }}>
                        Fetched {new Date(doc.fetched_at).toLocaleDateString('en-GB')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: theme.textTertiary, fontSize: 12, marginTop: 14, textAlign: 'center' }}>
        Showing {filtered.length} of {documents.length} documents
      </p>
    </div>
  );
};

export default RegulatoryMonitor;